import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PERIODS, PERIOD_LABEL, ROLE_LABEL, formatPhone, type PeriodKey, type UserRoleKey } from '@sc/shared/domain';
import { ExampleBadge, StageBadge } from '../components/badges';
import { api, ApiError, qs } from '../lib/api';
import { formatDateTime, formatInt, formatUsdMicro } from '../lib/format';
import { LeadDetailPanel } from './LeadDetailPanel';
import type { Page } from './types';

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : e ? 'Não foi possível concluir.' : null);

// ---------------------------------------------------------------------------- Conversas
interface ConversationRow {
  id: string;
  leadId: number;
  objective: 'instalar' | 'manutencao' | null;
  displayStage: Parameters<typeof StageBadge>[0]['stage'];
  model: string | null;
  turns: number;
  aiCalls: number;
  inputTokens: number;
  outputTokens: number;
  costMicroUsd: number;
  usedFallback: boolean;
  startedAt: string;
  isExample: boolean;
}

export function ConversationsPage() {
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['admin', 'conversations', period, page],
    queryFn: () => api.get<Page<ConversationRow>>(`/admin/conversations${qs({ period, page })}`),
  });
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.pageSize)) : 1;
  return (
    <div className="split">
      <main className="main">
        <header className="page-header">
          <h1>Conversas</h1>
          <div className="toolbar">
            <label className="sr-only" htmlFor="period">
              Período
            </label>
            <select id="period" className="pill" value={period} onChange={(e) => (setPeriod(e.target.value as PeriodKey), setPage(1))}>
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
        </header>
        <section className="card">
          <div className="card-head">
            <h2>Conversas do chat</h2>
            {q.data?.items.some((c) => c.isExample) && <ExampleBadge />}
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Lead</th>
                  <th scope="col">Início</th>
                  <th scope="col">Atendimento</th>
                  <th scope="col">Etapa</th>
                  <th scope="col">Respostas</th>
                  <th scope="col">Chamadas à IA</th>
                  <th scope="col">Custo</th>
                </tr>
              </thead>
              <tbody>
                {q.data?.items.map((c) => (
                  <tr key={c.id} aria-selected={selected === c.leadId} onClick={() => setSelected(c.leadId)}>
                    <td>
                      <button type="button" className="row-link" onClick={() => setSelected(c.leadId)} aria-label={`Abrir lead ${c.leadId}`}>
                        #{c.leadId}
                      </button>
                    </td>
                    <td className="small">{formatDateTime(c.startedAt)}</td>
                    <td>{c.objective === 'manutencao' ? 'Manutenção' : c.objective ? 'Instalação' : <span className="muted">—</span>}</td>
                    <td>
                      <StageBadge stage={c.displayStage} />
                    </td>
                    <td className="mono">{c.turns}</td>
                    <td className="mono">
                      {c.aiCalls}
                      {c.aiCalls > 0 && <span className="muted"> · {formatInt(c.inputTokens + c.outputTokens)} tokens</span>}
                    </td>
                    <td className="mono">{formatUsdMicro(c.costMicroUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {q.data && q.data.items.length === 0 && <p className="empty">Nenhuma conversa no período.</p>}
          </div>
          {q.data && q.data.total > q.data.pageSize && (
            <div className="pager">
              <span>
                página {page} de {pages}
              </span>
              <span className="toolbar">
                <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <button type="button" className="btn btn-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  Próxima
                </button>
              </span>
            </div>
          )}
        </section>
      </main>
      {selected !== null && <LeadDetailPanel leadId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------- Usuários
interface UserRow {
  id: string;
  email: string;
  name: string;
  role: UserRoleKey;
  mustChangePassword: boolean;
  active: boolean;
  locked: boolean;
  lastLoginAt: string | null;
}

export function UsersPage() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.get<{ items: UserRow[] }>('/admin/users') });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'comercial' as UserRoleKey });
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<{ user: { email: string }; temporaryPassword: string }>('/admin/users', form),
    onSuccess: (r) => {
      setSecret({ email: r.user.email, password: r.temporaryPassword });
      setCreating(false);
      setForm({ email: '', name: '', role: 'comercial' });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
  const reset = useMutation({
    mutationFn: (u: UserRow) => api.post<{ temporaryPassword: string }>(`/admin/users/${u.id}/reset-password`).then((r) => ({ u, r })),
    onSuccess: ({ u, r }) => {
      setSecret({ email: u.email, password: r.temporaryPassword });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
  const toggle = useMutation({
    mutationFn: (u: UserRow) => api.patch(`/admin/users/${u.id}`, { active: !u.active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  return (
    <main className="main">
      <header className="page-header">
        <h1>Usuários</h1>
        <button type="button" className="btn btn-primary" onClick={() => (setCreating(true), setSecret(null), create.reset())}>
          Adicionar usuário
        </button>
      </header>
      {secret && (
        <section className="card page-pad" role="status" aria-live="polite">
          <h2 className="section-title">Senha temporária de {secret.email}</h2>
          <p className="text">Copie e envie por um canal seguro. Ela não aparece de novo, e a pessoa troca no primeiro acesso.</p>
          <p className="secret">{secret.password}</p>
          <button type="button" className="btn btn-secondary" onClick={() => setSecret(null)}>
            Já copiei
          </button>
        </section>
      )}
      {creating && (
        <section className="card page-pad" aria-labelledby="user-form-title">
          <h2 id="user-form-title" className="section-title">
            Novo usuário
          </h2>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <div className="form-row">
              <div className="block">
                <label className="label" htmlFor="u-name">
                  Nome
                </label>
                <input id="u-name" className="control" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="block">
                <label className="label" htmlFor="u-email">
                  E-mail
                </label>
                <input id="u-email" className="control" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="block">
                <label className="label" htmlFor="u-role">
                  Papel
                </label>
                <select id="u-role" className="control" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as UserRoleKey })}>
                  <option value="comercial">Comercial (vê e atende os leads)</option>
                  <option value="admin">Administrador (também usuários e configurações)</option>
                </select>
              </div>
            </div>
            {create.error && (
              <p className="alert alert-error" role="alert">
                {errMsg(create.error)}
              </p>
            )}
            <div className="toolbar">
              <button className="btn btn-primary" type="submit" disabled={create.isPending}>
                Criar e gerar senha temporária
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setCreating(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </section>
      )}
      <section className="card">
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">E-mail</th>
                <th scope="col">Papel</th>
                <th scope="col">Situação</th>
                <th scope="col">Último acesso</th>
                <th scope="col">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.data?.items.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{ROLE_LABEL[u.role]}</td>
                  <td>
                    {!u.active ? (
                      <span className="badge tone-dark">Inativo</span>
                    ) : u.locked ? (
                      <span className="badge tone-red">Bloqueado por tentativas</span>
                    ) : u.mustChangePassword ? (
                      <span className="badge tone-yellow">Senha temporária</span>
                    ) : (
                      <span className="badge tone-green">Ativo</span>
                    )}
                  </td>
                  <td className="small">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Nunca'}</td>
                  <td className="toolbar">
                    <button type="button" className="btn-link" onClick={() => reset.mutate(u)}>
                      Redefinir senha
                    </button>
                    <button type="button" className="btn-link" onClick={() => toggle.mutate(u)}>
                      {u.active ? 'Desativar' : 'Reativar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(toggle.error || reset.error) && (
          <p className="alert alert-error page-pad" role="alert">
            {errMsg(toggle.error ?? reset.error)}
          </p>
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------- Configurações
interface Regras {
  contaMinima: number;
  contaMinimaForaSp: number;
  contaSenior: number;
}
interface ReviewsSettings {
  rating: number;
  count: number;
  profileUrl: string;
  items: { author: string; text: string }[];
}
interface Analysis {
  tarifa: number;
  horasSol: number;
  eficiencia: number;
  potenciaPainelW: number;
  areaPainelM2: number;
  economiaMin: number;
  economiaMax: number;
  precoKwpMin: number | null;
  precoKwpMax: number | null;
}
interface Settings {
  whatsapp: string | null;
  notificationEmails: string[];
  whatsappIntro: string | null;
  reviews: ReviewsSettings | null;
  analysis: Analysis;
  analysisDefaults: Analysis;
  rules: Regras;
  defaults: Regras;
  ai: { provider: string; model: string; enabled: boolean; rewrite: boolean; dailyBudgetUsd: number };
  mailConfigured: boolean;
  embedOrigins: string[];
}

const numero = (v: string) => Number(v.replace(/\./g, '').replace(',', '.'));

export function SettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'settings'], queryFn: () => api.get<Settings>('/admin/settings') });
  const s = q.data;
  const [form, setForm] = useState({ whatsapp: '', emails: '', intro: '', contaMinima: '', contaMinimaForaSp: '', contaSenior: '' });
  const [saved, setSaved] = useState(false);
  const [rv, setRv] = useState({ rating: '', count: '', profileUrl: '' });
  const [items, setItems] = useState<{ author: string; text: string }[]>([]);
  const [an, setAn] = useState<Record<string, string>>({});
  const dec = (n: number | null | undefined, casas = 2) => (n == null ? '' : n.toLocaleString('pt-BR', { maximumFractionDigits: casas }));

  useEffect(() => {
    if (!s) return;
    setRv({
      rating: s.reviews ? s.reviews.rating.toLocaleString('pt-BR', { minimumFractionDigits: 1 }) : '',
      count: s.reviews ? String(s.reviews.count) : '',
      profileUrl: s.reviews?.profileUrl ?? '',
    });
    setItems(s.reviews?.items ?? []);
    setAn({
      tarifa: dec(s.analysis.tarifa),
      horasSol: dec(s.analysis.horasSol, 1),
      eficiencia: dec(s.analysis.eficiencia * 100, 0),
      potenciaPainelW: String(s.analysis.potenciaPainelW),
      areaPainelM2: dec(s.analysis.areaPainelM2),
      economiaMin: dec(s.analysis.economiaMin * 100, 0),
      economiaMax: dec(s.analysis.economiaMax * 100, 0),
      precoKwpMin: dec(s.analysis.precoKwpMin, 0),
      precoKwpMax: dec(s.analysis.precoKwpMax, 0),
    });
    setForm({
      whatsapp: s.whatsapp ? formatPhone(s.whatsapp) : '',
      emails: s.notificationEmails.join(', '),
      intro: s.whatsappIntro ?? '',
      contaMinima: String(s.rules.contaMinima),
      contaMinimaForaSp: String(s.rules.contaMinimaForaSp),
      contaSenior: String(s.rules.contaSenior),
    });
  }, [s]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<Settings>('/admin/settings', {
        whatsapp: form.whatsapp,
        notificationEmails: form.emails
          .split(/[,;\s]+/)
          .map((e) => e.trim())
          .filter(Boolean),
        whatsappIntro: form.intro.trim(),
        rules: { contaMinima: numero(form.contaMinima), contaMinimaForaSp: numero(form.contaMinimaForaSp), contaSenior: numero(form.contaSenior) },
        analysis: {
          tarifa: numero(an.tarifa ?? ''),
          horasSol: numero(an.horasSol ?? ''),
          eficiencia: numero(an.eficiencia ?? '') / 100,
          potenciaPainelW: Math.round(numero(an.potenciaPainelW ?? '')),
          areaPainelM2: numero(an.areaPainelM2 ?? ''),
          economiaMin: numero(an.economiaMin ?? '') / 100,
          economiaMax: numero(an.economiaMax ?? '') / 100,
          precoKwpMin: an.precoKwpMin?.trim() ? numero(an.precoKwpMin) : null,
          precoKwpMax: an.precoKwpMax?.trim() ? numero(an.precoKwpMax) : null,
        },
        ...(rv.profileUrl.trim()
          ? {
              reviews: {
                rating: numero(rv.rating),
                count: Math.round(numero(rv.count)),
                profileUrl: rv.profileUrl.trim(),
                items: items.filter((i) => i.author.trim() && i.text.trim()),
              },
            }
          : {}),
      }),
    onSuccess: (r) => {
      qc.setQueryData(['admin', 'settings'], r);
      setSaved(true);
    },
    onError: () => setSaved(false),
  });

  const field = (k: keyof typeof form, rotulo: string, ajuda: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <div className="block">
      <label className="label" htmlFor={`s-${k}`}>
        {rotulo}
      </label>
      <input id={`s-${k}`} className="control" value={form[k]} onChange={(e) => (setForm({ ...form, [k]: e.target.value }), setSaved(false))} {...props} />
      <span className="small">{ajuda}</span>
    </div>
  );

  return (
    <main className="main">
      <header className="page-header">
        <h1>Configurações</h1>
      </header>
      {s && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <section className="card page-pad">
            <h2 className="section-title">Para onde vão os leads</h2>
            <div className="form">
              {field('whatsapp', 'WhatsApp da Sun Coast', 'Número que recebe a conversa quando o lead termina a triagem.', { inputMode: 'tel', required: true })}
              {field('emails', 'E-mails de aviso', 'Recebem um e-mail a cada lead novo. Separe por vírgula.', { inputMode: 'email' })}
              {field('intro', 'Abertura da mensagem do WhatsApp', 'Primeira linha da mensagem que o lead envia. O resumo da triagem vem logo abaixo.', { maxLength: 300 })}
            </div>
          </section>
          <section className="card page-pad">
            <h2 className="section-title">Regras da triagem</h2>
            <p className="small">Valem na hora para as conversas novas. Valores da conta de luz por mês, em reais.</p>
            <div className="form-row">
              {field('contaMinima', 'Conta mínima', `Abaixo disso a conversa encerra. Padrão: ${s.defaults.contaMinima}.`, { inputMode: 'numeric' })}
              {field('contaMinimaForaSp', 'Conta mínima fora de SP', `Fora do estado, só acima disso. Padrão: ${s.defaults.contaMinimaForaSp}.`, { inputMode: 'numeric' })}
              {field('contaSenior', 'Conta para consultor sênior', `A partir disso (ou indústria e rural). Padrão: ${s.defaults.contaSenior}.`, { inputMode: 'numeric' })}
            </div>
          </section>
          <section className="card page-pad">
            <h2 className="section-title">Pré-análise do projeto</h2>
            <p className="small">
              Calcula o sistema indicado e a economia que o chat mostra antes do formulário. Confira a tarifa numa conta recente da Enel (valor total ÷ kWh) e as
              horas de sol no atlas solar (CRESESB).
            </p>
            <div className="form-row">
              {(
                [
                  ['tarifa', 'Tarifa com impostos (R$/kWh)', `Padrão ${dec(s.analysisDefaults.tarifa)}`],
                  ['horasSol', 'Horas de sol por dia', `Padrão ${dec(s.analysisDefaults.horasSol, 1)}`],
                  ['eficiencia', 'Eficiência do sistema (%)', `Padrão ${dec(s.analysisDefaults.eficiencia * 100, 0)}`],
                  ['potenciaPainelW', 'Potência do painel (W)', `Padrão ${s.analysisDefaults.potenciaPainelW}`],
                  ['areaPainelM2', 'Área por painel (m²)', `Padrão ${dec(s.analysisDefaults.areaPainelM2)}`],
                ] as const
              ).map(([k, rotulo, ajuda]) => (
                <div className="block" key={k}>
                  <label className="label" htmlFor={`an-${k}`}>
                    {rotulo}
                  </label>
                  <input id={`an-${k}`} className="control" inputMode="decimal" value={an[k] ?? ''} onChange={(e) => (setAn({ ...an, [k]: e.target.value }), setSaved(false))} />
                  <span className="small">{ajuda}</span>
                </div>
              ))}
            </div>
            <div className="form-row">
              {(
                [
                  ['economiaMin', 'Economia mínima (% da conta)', 'O que sobra é taxa mínima e Fio B'],
                  ['economiaMax', 'Economia máxima (% da conta)', `Padrão ${dec(s.analysisDefaults.economiaMin * 100, 0)} a ${dec(s.analysisDefaults.economiaMax * 100, 0)}`],
                  ['precoKwpMin', 'Preço por kWp, mínimo (R$)', 'Opcional'],
                  ['precoKwpMax', 'Preço por kWp, máximo (R$)', 'Vazio: o chat não mostra investimento nem retorno'],
                ] as const
              ).map(([k, rotulo, ajuda]) => (
                <div className="block" key={k}>
                  <label className="label" htmlFor={`an-${k}`}>
                    {rotulo}
                  </label>
                  <input id={`an-${k}`} className="control" inputMode="decimal" value={an[k] ?? ''} onChange={(e) => (setAn({ ...an, [k]: e.target.value }), setSaved(false))} />
                  <span className="small">{ajuda}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="card page-pad">
            <h2 className="section-title">Avaliações no Google</h2>
            <p className="small">
              Aparecem no chat: cartão na lateral do computador e selo no celular. Os termos do Google não permitem copiar a nota automaticamente, então
              confira de vez em quando no{' '}
              {rv.profileUrl ? (
                <a href={rv.profileUrl} target="_blank" rel="noreferrer noopener">
                  perfil do Google
                </a>
              ) : (
                'perfil do Google'
              )}{' '}
              e atualize aqui. Use só avaliações reais, com o nome como aparece no Google.
            </p>
            <div className="form-row">
              <div className="block">
                <label className="label" htmlFor="rv-rating">
                  Nota
                </label>
                <input id="rv-rating" className="control" inputMode="decimal" value={rv.rating} onChange={(e) => (setRv({ ...rv, rating: e.target.value }), setSaved(false))} />
              </div>
              <div className="block">
                <label className="label" htmlFor="rv-count">
                  Nº de avaliações
                </label>
                <input id="rv-count" className="control" inputMode="numeric" value={rv.count} onChange={(e) => (setRv({ ...rv, count: e.target.value }), setSaved(false))} />
              </div>
              <div className="block">
                <label className="label" htmlFor="rv-url">
                  Link do perfil no Google
                </label>
                <input id="rv-url" className="control" type="url" value={rv.profileUrl} onChange={(e) => (setRv({ ...rv, profileUrl: e.target.value }), setSaved(false))} />
              </div>
            </div>
            {items.map((it, n) => (
              <div className="form-row" key={n}>
                <div className="block">
                  <label className="label" htmlFor={`rv-a-${n}`}>
                    Autor {n + 1}
                  </label>
                  <input
                    id={`rv-a-${n}`}
                    className="control"
                    maxLength={80}
                    value={it.author}
                    onChange={(e) => (setItems(items.map((x, k) => (k === n ? { ...x, author: e.target.value } : x))), setSaved(false))}
                  />
                </div>
                <div className="block" style={{ flex: 3 }}>
                  <label className="label" htmlFor={`rv-t-${n}`}>
                    Avaliação {n + 1}
                  </label>
                  <textarea
                    id={`rv-t-${n}`}
                    className="control"
                    maxLength={400}
                    value={it.text}
                    onChange={(e) => (setItems(items.map((x, k) => (k === n ? { ...x, text: e.target.value } : x))), setSaved(false))}
                  />
                </div>
                <button type="button" className="btn-link" onClick={() => (setItems(items.filter((_, k) => k !== n)), setSaved(false))}>
                  Remover
                </button>
              </div>
            ))}
            {items.length < 6 && (
              <button type="button" className="btn btn-secondary" onClick={() => setItems([...items, { author: '', text: '' }])}>
                Adicionar avaliação
              </button>
            )}
          </section>
          {save.error && (
            <p className="alert alert-error" role="alert">
              {errMsg(save.error)}
            </p>
          )}
          {saved && !save.error && (
            <p className="alert alert-ok" role="status">
              Configurações salvas.
            </p>
          )}
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar configurações'}
          </button>
        </form>
      )}
      {s && (
        <section className="card page-pad">
          <h2 className="section-title">Servidor</h2>
          <p className="small">Estes vêm das variáveis de ambiente. Para mudar, ajuste o .env e reinicie a API.</p>
          <ul className="list-plain">
            <li>
              IA: {s.ai.enabled ? <span className="badge tone-green">Ligada</span> : <span className="badge tone-yellow">Desligada (só roteiro)</span>} ·{' '}
              <span className="mono">{s.ai.model}</span>
            </li>
            <li>Reescrita das perguntas pela IA: {s.ai.rewrite ? 'ligada' : 'desligada (mais barato)'}</li>
            <li>Teto de gasto por dia: US$ {s.ai.dailyBudgetUsd.toLocaleString('pt-BR')}</li>
            <li>E-mail (Resend): {s.mailConfigured ? <span className="badge tone-green">Configurado</span> : <span className="badge tone-yellow">Pendente</span>}</li>
            <li>Sites que podem mostrar o balão: {s.embedOrigins.length ? s.embedOrigins.join(', ') : <span className="badge tone-yellow">Nenhum</span>}</li>
          </ul>
        </section>
      )}
    </main>
  );
}
